// components/QualificationDetails.jsx
import React, { useState } from "react";
import { FaPlus, FaUpload, FaTrash } from "react-icons/fa";

// ✅ Match backend EducationLevel enum EXACTLY
const DEGREE_OPTIONS = [
  "10th",
  "12th",
  "Diploma",
  "Bachelor's",        // ✅ Changed from "Bachelor"
  "Master's",          // ✅ Changed from "Masters"
  "Training/Internship",
  "Other",             // ✅ Changed from "Others"
];

export default function QualificationDetails({
  data,
  update,
  nextStep,
  prevStep,
  isLastStep,
  onFinalSubmit,
}) {
  const [submitError, setSubmitError] = useState("");

  const safeData = Array.isArray(data) ? data : [];
  const safePrevStep = typeof prevStep === "function" ? prevStep : () => {};

  const addQualification = () => {
    update([
      ...safeData,
      {
        id: Date.now(),
        schoolName: "",
        degree: "",
        customDegree: "",
        specialization: "",
        percentage: "",
        year: "",
        file: null,
      },
    ]);
  };

  const deleteQualification = (id) => {
    if (safeData.length === 1) {
      setSubmitError("At least one qualification is required.");
      return;
    }
    update(safeData.filter((q) => q.id !== id));
  };

  const updateItem = (id, field, value) => {
    update(
      safeData.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const handleFileChange = (id, file) => {
    updateItem(id, "file", file);
  };

  const validateQualifications = () => {
    setSubmitError("");

    if (safeData.length === 0) {
      setSubmitError("Please add at least one qualification.");
      return false;
    }

    for (const qual of safeData) {
      if (!qual.schoolName.trim()) {
        setSubmitError("Please enter institution name for all qualifications.");
        return false;
      }
      if (!qual.degree) {
        setSubmitError("Please select degree for all qualifications.");
        return false;
      }
      // ✅ Validate custom degree if "Other" selected
      if (qual.degree === "Other" && !qual.customDegree?.trim()) {
        setSubmitError("Please specify the degree type for 'Other' option.");
        return false;
      }
      if (!qual.percentage) {
        setSubmitError("Please enter percentage for all qualifications.");
        return false;
      }
      if (!qual.year) {
        setSubmitError("Please enter year of passing for all qualifications.");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = () => {
    if (!validateQualifications()) {
      return;
    }

    if (isLastStep) {
      onFinalSubmit();
    } else {
      nextStep();
    }
  };

  return (
    <div className="space-y-8 mt-4">
      {safeData.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-xl">
          <p className="text-gray-500">No qualifications added yet</p>
          <p className="text-sm text-gray-400 mt-2">
            Click "Add qualification" to get started
          </p>
        </div>
      ) : (
        safeData.map((item, index) => (
          <div
            key={item.id}
            className="space-y-6 p-6 bg-[#F9F9F9] rounded-[20px] border border-gray-100"
          >
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                Qualification {index + 1}
              </h3>
              {safeData.length > 1 && (
                <button
                  onClick={() => deleteQualification(item.id)}
                  className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-full"
                  title="Delete qualification"
                >
                  <FaTrash />
                </button>
              )}
            </div>

            {/* ROW 1: School Name + Degree Dropdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm text-[#666666] font-medium block">
                  School / College / University Name*
                </label>
                <input
                  className="w-full h-12 px-4 rounded-[10px] bg-white border border-gray-200 focus:border-[#0CD974] outline-none"
                  value={item.schoolName}
                  onChange={(e) =>
                    updateItem(item.id, "schoolName", e.target.value)
                  }
                  placeholder="Enter institution name"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-[#666666] font-medium block">
                  Qualification / Degree*
                </label>
                <select
                  value={item.degree}
                  onChange={(e) =>
                    updateItem(item.id, "degree", e.target.value)
                  }
                  className="w-full h-12 px-4 rounded-[10px] bg-white border border-gray-200 focus:border-[#0CD974] outline-none"
                >
                  <option value="">Select Degree</option>
                  {DEGREE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Conditional: Show text input if "Other" is selected */}
            {item.degree === "Other" && (
              <div className="space-y-2">
                <label className="text-sm text-[#666666] font-medium block">
                  Specify Degree*
                </label>
                <input
                  className="w-full h-12 px-4 rounded-[10px] bg-white border border-gray-200 focus:border-[#0CD974] outline-none"
                  value={item.customDegree || ""}
                  onChange={(e) =>
                    updateItem(item.id, "customDegree", e.target.value)
                  }
                  placeholder="Enter degree name"
                />
              </div>
            )}

            {/* ROW 2: Specialization + Percentage + Year */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-sm text-[#666666] font-medium block">
                  Specialization (Optional)
                </label>
                <input
                  className="w-full h-12 px-4 rounded-[10px] bg-white border border-gray-200 focus:border-[#0CD974] outline-none"
                  value={item.specialization || ""}
                  onChange={(e) =>
                    updateItem(item.id, "specialization", e.target.value)
                  }
                  placeholder="e.g., Computer Science"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-[#666666] font-medium block">
                  Percentage (%)*
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-full h-12 px-4 rounded-[10px] bg-white border border-gray-200 focus:border-[#0CD974] outline-none"
                  value={item.percentage}
                  onChange={(e) =>
                    updateItem(item.id, "percentage", e.target.value)
                  }
                  placeholder="Percentage"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-[#666666] font-medium block">
                  Year of Passing*
                </label>
                <input
                  type="number"
                  min="1950"
                  max={new Date().getFullYear()}
                  className="w-full h-12 px-4 rounded-[10px] bg-white border border-gray-200 focus:border-[#0CD974] outline-none"
                  value={item.year}
                  onChange={(e) => updateItem(item.id, "year", e.target.value)}
                  placeholder="Year"
                />
              </div>
            </div>

            {/* ROW 3: Upload Documents */}
            <div className="space-y-2">
              <label className="text-sm text-[#666666] font-medium block">
                Upload Supported Documents (Optional)
              </label>

              <input
                type="file"
                id={`file-${item.id}`}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange(item.id, e.target.files[0])}
              />

              <label
                htmlFor={`file-${item.id}`}
                className="flex items-center justify-center gap-2 w-full h-12 rounded-[10px] border border-dashed border-gray-300 bg-white cursor-pointer hover:border-[#0CD974] hover:bg-green-50 transition-all"
              >
                <FaUpload className="text-gray-400" />
                <span className="text-sm text-gray-500 truncate max-w-[220px]">
                  {item.file ? item.file.name : "Click to upload"}
                </span>
              </label>

              <div className="flex justify-between text-xs text-gray-400">
                <span>PDF / JPG / PNG allowed</span>
                {item.file && (
                  <button
                    type="button"
                    onClick={() => handleFileChange(item.id, null)}
                    className="text-red-500 hover:text-red-700"
                  >
                    Remove file
                  </button>
                )}
              </div>
            </div>
          </div>
        ))
      )}

      <div className="flex justify-center">
        <button
          onClick={addQualification}
          type="button"
          className="text-[#0CD974] hover:text-[#0bb963] font-medium flex items-center gap-2 px-4 py-2 border border-dashed border-[#0CD974] rounded-lg hover:bg-green-50 transition-all"
        >
          <FaPlus className="w-4 h-4" />
          Add another qualification
        </button>
      </div>

      <div className="flex justify-between mt-6 pt-6 border-t border-gray-100">
        <button
          onClick={safePrevStep}
          className="px-8 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
        >
          Previous
        </button>

        <button
          onClick={handleSubmit}
          className="px-8 py-3 rounded-lg bg-[#0CD974] text-white font-medium hover:bg-[#0bb963] transition-colors shadow-md"
        >
          {isLastStep ? "Submit All" : "Save & Continue"}
        </button>
      </div>
      {submitError && (
        <div className="fixed bottom-6 left-6 z-50 max-w-sm bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg">
          <p className="text-sm font-medium">{submitError}</p>
        </div>
      )}
    </div>
  );
}
